import { administrationEditTranslations as text } from 'app/area/administration/edit/constants';
import * as navigate from 'app/area/shared/navigation';
import { STATE } from 'app/constants';
import { errorSafe } from 'app/infrastructure/redux';
import * as simpleFormActions from 'app/infrastructure/simple-form/actions';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { SuccessNotification, ErrorNotification, InfoNotification } from 'app/infrastructure/notifications/user-messages';
import { api } from './api';
import { editApi } from '../api';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { sharedApi } from 'app/area/administration/shared/api';
import { createValidationErrors } from 'ui';
import { AccessItem, AccessPanelDataValidation } from 'app/components/access-panel/shared/types';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import {
  ClearUserData, RequestLoadUser, ReceiveLoadUser, FailLoadUser,
  RequestLoadUserAccess, ReceiveLoadUserAccess, FailLoadUserAccess,
  RequestAddUser, RequestUpdateUser, ReceiveAddUser, ReceiveUpdateUser, FailUserExecute,
  RequestChangePersonId, ReceiveChangePersonId, FailChangePersonId,
  RequestExtraInfo, ReceiveExtraInfo, FailExtraInfo,
  RequestPersonOptions, ReceivePersonOptions, FailPersonOptions,
  RequestRole, ReceiveRole, FailRole, UpdateAccessData,
  RequestRolePanelAccess, ReceiveRolePanelAccess, FailRolePanelAccess,
  RequestChangeDatabaseAccount, ReceiveChangeDatabaseAccount, FailChangeDatabaseAccount,
  ProcessUserFailed, ClearValidation,
  RequestDefaultRole, ReceiveDefaultRole, FailDefaultRole, ShowTechnicalUserWarning, HideTechnicalUserWarning,
  RequestCheckRoles, ReceiveCheckRoles, FailCheckRoles
} from './action-types';
import { dispatchSaveError, dispatchValidationError } from 'app/area/administration/edit/user/actions.util';
import { UserInfoNotification } from 'app/area/administration/edit/user/types';
import { mapFromDefaultRole } from 'app/area/administration/edit/user/default-role-mapper';

const simpleFormActionCreators = simpleFormActions.actionCreators(STATE.administrationEdit);

export const actionCreators = {

  clearValueInStore: (id: string): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(simpleFormActionCreators.clearValueInStore(id));
    }),

  loadExtraInfo: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestExtraInfo());
      try {
        const extraInfo = await editApi.getExtraInfo();
        dispatch(new ReceiveExtraInfo(extraInfo.data));
      } catch (err) {
        dispatch(new FailExtraInfo());
      }
    },

  loadPersonOptions: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestPersonOptions());
      try {
        const personOptions = await editApi.getPersonOptions();
        dispatch(new ReceivePersonOptions(personOptions.rootCodes));
      } catch (err) {
        dispatch(new FailPersonOptions());
      }
    },

  loadRole: (id: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      if (id === undefined) {
        dispatch(new ReceiveRole(id, undefined));
        return;
      }

      dispatch(new RequestRole(id));

      try {
        const role = await api.getRole(id);
        dispatch(new ReceiveRole(id, role.data));
      } catch (err) {
        dispatch(new FailRole(id));
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
      }
    },

  loadRolePanelAccess: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestRolePanelAccess());
      try {
        const userRoleOptions = await api.getRoleOptions();
        dispatch(new ReceiveRolePanelAccess(userRoleOptions));
      } catch (err) {
        dispatch(new FailRolePanelAccess());
      }
    },

  loadUser: (id: number, updatedData: { [key: string]: string } = {}): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        dispatch(new RequestLoadUser());

        const result = await api.getUser(id);

        dispatch(new ReceiveLoadUser({ ...result.data, ...updatedData }));
      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationEdit,
            translationId: text.no_access_to_user_administration
          });
        } else {
          navigate.toAdminUsersList();
        }

        dispatch(new FailLoadUser());
      }
    },

  loadUserAccess: (id: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        dispatch(new RequestLoadUserAccess());

        const result = await sharedApi.userAccess(id) as any;

        result && dispatch(new ReceiveLoadUserAccess(result[0]));
      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationEdit,
            translationId: text.no_access_to_user_administration
          });
        } else {
          navigate.toAdminUsersList();
        }

        dispatch(new FailLoadUserAccess());
      }
    },

  addUser: (user): ActionDispatching<any, any> => async (dispatch: any, getState: any) => {
    const translate = createDescriptionSelector(getState().administrationEditArea.texts);
    try {
      dispatch(new RequestAddUser());
      const result = await api.addUser(user) as any;

      if (result) {
        dispatch(new ReceiveAddUser());
        dispatch(simpleFormActionCreators.resetChangesListener());
        dispatch(new SuccessNotification(translate(text.added_user)));
        navigate.toAdminUsersList();
      }
    } catch (err) {
      dispatch(new FailUserExecute());
      dispatchSaveError(err, dispatch, translate(text.validation_error), user, getState);
    }
  },

  duplicateUser: (user): ActionDispatching<any, any> => async (dispatch: any, getState: any) => {
    const translate = createDescriptionSelector(getState().administrationEditArea.texts);
    try {
      dispatch(new RequestAddUser());
      const result = await api.addUser(user) as any;

      if (result) {
        dispatch(new ReceiveAddUser());
        dispatch(simpleFormActionCreators.resetChangesListener());
        dispatch(new SuccessNotification(translate(text.duplicated_user)));
        navigate.toAdminUsersList();
      }
    } catch (err) {
      dispatch(new FailUserExecute());
      dispatchSaveError(err, dispatch, translate(text.validation_error), user, getState);
    }
  },

  updateUser: (id, query): ActionDispatching<any, any> => async (dispatch: any, getState: () => ApplicationState) => {
    const translate = createDescriptionSelector(getState().administrationEditArea.texts);

    try {
      dispatch(new RequestUpdateUser());
      const result = await api.updateUser(id, {
        ...query,
        emailAddress: !query.emailAddress ? null : query.emailAddress
      }) as any;

      if (result) {
        dispatch(new ReceiveUpdateUser());
        dispatch(simpleFormActionCreators.resetChangesListener());
        dispatch(new SuccessNotification(translate(text.updated_user)));
        navigate.toAdminUsersList();
      }
    } catch (err) {
      dispatch(new FailUserExecute());

      if (isNotFoundStatus(err)) {
        navigate.toAdminUsersList();
      }

      dispatchSaveError(err, dispatch, translate(text.validation_error), query, getState);
    }
  },

  showTechnicalUserWarning: (): ActionDispatching<any, any> => async (dispatch: any, getState: () => ApplicationState) => {
    dispatch(new ShowTechnicalUserWarning());
  },

  hideTechnicalUserWarning: (): ActionDispatching<any, any> => async (dispatch: any, getState: () => ApplicationState) => {
    dispatch(new HideTechnicalUserWarning());
  },

  deleteUser: (id: number): ActionDispatching<any, any> => errorSafe(
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationEditArea.texts);
      await sharedApi.deleteUser(id);
      navigate.toAdminUsersList();
      dispatch(new SuccessNotification(translate(text.user_successfully_deleted)));
    }),

  changePassword: (id: number, password: string, onSuccess: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      try {
        const result = await api.changePassword(id, password) as any;

        if (result) {
          dispatch(new SuccessNotification(result.json.messages.userMessages));
          onSuccess();
        }
      } catch (err) {
        if (!!err.validationErrors && !!err.validationErrors.password && !!err.validationErrors.password.length) {
          const state = getState().administrationEditArea;
          dispatch(new ProcessUserFailed({
            ...state.validation,
            password: createValidationErrors(err.validationErrors.password)
          }));
        }

        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
      }
    },

  changePersonId: (id: number, personId: string, onSuccess: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      try {

        dispatch(new RequestChangePersonId());

        await api.changePersonId(id, personId);

        dispatch(new ReceiveChangePersonId(personId));

        const translate = createDescriptionSelector(getState().administrationEditArea.texts);
        dispatch(new SuccessNotification(translate(text.user_id_changed)));

        onSuccess();

      } catch (err) {
        dispatchValidationError(err, dispatch, getState);
        dispatch(new FailChangePersonId());
      }
    },

  updateAccessData: (key: string, accessItem: Partial<AccessItem>): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new UpdateAccessData(key, accessItem));
    },

  saveDatabaseAccount: (id: number, password: string, onSuccess: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationEditArea.texts);
      dispatch(new RequestChangeDatabaseAccount());

      try {

        const result = await api.saveDatabaseAccount(id, password) as any;

        if (result) {
          await dispatch(actionCreators.loadUserAccess(id));
          onSuccess();
          dispatch(new SuccessNotification(translate(text.database_user_create_message)));
          dispatch(new ReceiveChangeDatabaseAccount());
        }
      } catch (err) {
        dispatchValidationError(err, dispatch, getState);
        dispatch(new FailChangeDatabaseAccount());
      }
    },

  resetDatabaseAccount: (id: number, password: string, onSuccess: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationEditArea.texts);
      dispatch(new RequestChangeDatabaseAccount());
      try {

        const result = await api.resetDatabaseAccount(id, password) as any;

        if (result) {
          await dispatch(actionCreators.loadUserAccess(id));
          onSuccess();
          dispatch(new SuccessNotification(translate(text.database_user_reset_message)));
          dispatch(new ReceiveChangeDatabaseAccount());
        }
      } catch (err) {
        dispatchValidationError(err, dispatch, getState);
        dispatch(new FailChangeDatabaseAccount());
      }
    },

  deleteDatabaseAccount: (id: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestChangeDatabaseAccount());
      const translate = createDescriptionSelector(getState().administrationEditArea.texts);
      try {
        const result = await api.deleteDatabaseAccount(id);
        if (result) {
          await dispatch(actionCreators.loadUserAccess(id));
          dispatch(new ReceiveChangeDatabaseAccount());
          dispatch(new SuccessNotification(translate(text.database_user_delete_message)));
        }
      } catch (err) {
        dispatchValidationError(err, dispatch, getState);
        dispatch(new FailChangeDatabaseAccount());
      }
    },

  validateCustomData: (accessPanelDataValidation: AccessPanelDataValidation): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationEditArea.texts);
      dispatch(new ErrorNotification(translate(text.validation_error)));
      dispatch(new ProcessUserFailed(accessPanelDataValidation || []));
      dispatch(new FailUserExecute());
    },

  clearValidation: (formName: string, parameters?: { [key: string]: number | string }): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new ClearValidation(formName, parameters));
    },

  clearAllValues: (): ActionDispatching<any, any> => async (dispatch: any, getState: any) => {
    dispatch(new ClearUserData());
    dispatch(simpleFormActionCreators.clearAllValues());
  },

  showUserInfoNotifications: (notifications: UserInfoNotification[] = []): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      notifications.forEach(notification => notification.show && dispatch(new InfoNotification(notification.message)));
    },

  loadDefaultRole: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestDefaultRole());
      try {
        const defaultRole = await sharedApi.getDefaultRole();
        dispatch(new ReceiveDefaultRole(mapFromDefaultRole(defaultRole)));
      } catch (err) {
        dispatch(new FailDefaultRole());
      }
    },

  checkRoles: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => any) => {
      dispatch(new RequestCheckRoles());
      try {
        const roles = await sharedApi.getRoles();
        dispatch(new ReceiveCheckRoles(roles.length > 0));
      } catch (err) {
        dispatch(new FailCheckRoles());
      }
    }
};
